import { Paper, Text, Group, ThemeIcon, Skeleton } from '@mantine/core';
import { IconTrendingUp, IconTrendingDown, IconMinus } from '@tabler/icons-react';
import type { ReactNode } from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: number;
  trendLabel?: string;
  loading?: boolean;
  color?: string;
}

export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendLabel,
  loading,
  color = 'blue',
}: MetricCardProps) {
  const getTrendIcon = () => {
    if (trend === undefined || trend === 0) return <IconMinus size={14} />;
    return trend > 0 ? <IconTrendingUp size={14} /> : <IconTrendingDown size={14} />;
  };

  const getTrendColor = () => {
    if (trend === undefined || trend === 0) return 'gray';
    return trend > 0 ? 'green' : 'red';
  };

  if (loading) {
    return (
      <Paper withBorder p="md">
        <Skeleton height={16} width={100} mb="sm" />
        <Skeleton height={32} width={120} mb="xs" />
        <Skeleton height={14} width={80} />
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed" fw={500}>
          {title}
        </Text>
        {icon && (
          <ThemeIcon variant="light" color={color} size="sm">
            {icon}
          </ThemeIcon>
        )}
      </Group>

      <Text size="xl" fw={700}>
        {value}
      </Text>

      {(subtitle || trend !== undefined) && (
        <Group gap="xs" mt="xs">
          {trend !== undefined && (
            <Group gap={4}>
              <ThemeIcon variant="light" color={getTrendColor()} size="xs">
                {getTrendIcon()}
              </ThemeIcon>
              <Text size="xs" c={getTrendColor()}>
                {trend > 0 ? '+' : ''}
                {trend.toFixed(1)}%
              </Text>
              {trendLabel && (
                <Text size="xs" c="dimmed">
                  {trendLabel}
                </Text>
              )}
            </Group>
          )}
          {subtitle && !trend && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Group>
      )}
    </Paper>
  );
}
